/**
 * paymentSession.ts — fail-closed stub vs real Stripe Elements decision, plus
 * the one question that decides whether the session on hand is still the right
 * one to pay against.
 *
 * Stub mode is ONLY when Orders explicitly returns `paymentSession.stub === true`.
 * Missing/blank/malformed `clientSecret` on a real session must NEVER fall through
 * to stub finalisation.
 */
import type { Address } from '../api/types';

/** Stripe SetupIntent client secrets look like `seti_…_secret_…`. */
export function isValidSetupIntentClientSecret(clientSecret: string | null | undefined): boolean {
  if (typeof clientSecret !== 'string') return false;
  const trimmed = clientSecret.trim();
  if (!trimmed) return false;
  // Fail closed on obvious placeholders / wrong object types.
  if (!trimmed.startsWith('seti_')) return false;
  if (!trimmed.includes('_secret_')) return false;
  return true;
}

export type PaymentSessionGrantLike = {
  stub: boolean;
  setupIntentId: string;
  clientSecret?: string;
};

export type PaymentSessionDecision =
  | { kind: 'stub'; setupIntentId: string }
  | { kind: 'elements'; setupIntentId: string; clientSecret: string }
  | { kind: 'invalid_real_session' };

/**
 * Decide stub vs Elements vs hard fail. Never infers stub from a missing secret.
 */
export function decidePaymentSessionFlow(grant: PaymentSessionGrantLike): PaymentSessionDecision {
  if (grant.stub === true) {
    return { kind: 'stub', setupIntentId: grant.setupIntentId };
  }
  // stub === false (or any non-true): real session — clientSecret is mandatory.
  if (!isValidSetupIntentClientSecret(grant.clientSecret)) {
    return { kind: 'invalid_real_session' };
  }
  return {
    kind: 'elements',
    setupIntentId: grant.setupIntentId,
    clientSecret: grant.clientSecret!.trim(),
  };
}

/**
 * Do these two delivery addresses describe the same place?
 *
 * WHY THIS EXISTS. A checkout session is minted for ONE delivery address:
 * `POST checkout/session` writes `deliveryAddress` onto the OrderCase, and it is
 * the ONLY call that does — nothing later re-reads the address the browser
 * holds. So a portal that reuses a session it already has, after the patient has
 * gone back and corrected their address, ships a prescription-only medicine to
 * the address they just replaced. The patient sees the right address on screen
 * and the courier takes it somewhere else, which is both a lost order and
 * someone else's front door receiving their medicine.
 *
 * Compared FIELD BY FIELD rather than through `formatAddress`, which drops empty
 * parts and would call "12 High St, Leeds" and "12, High St, Leeds" the same
 * address. This predicate is only ever used to decide whether to re-send, so it
 * must err towards DIFFERENT: a redundant re-send is idempotent on the server
 * (orders reuses the same OrderCase), while a missed one delivers to the wrong
 * house.
 *
 * A missing `line2` and an empty `line2` are the same address — that difference
 * is the form, not the place.
 */
export function sameDeliveryAddress(a: Address | null | undefined, b: Address | null | undefined): boolean {
  if (!a || !b) return a === b;
  return (
    a.line1 === b.line1 &&
    (a.line2 ?? '') === (b.line2 ?? '') &&
    a.city === b.city &&
    a.postcode === b.postcode
  );
}
